import React, { useMemo, useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Button,
    MenuItem,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    InputAdornment,
    Avatar,
    Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useEntidade } from "../../Hooks/EntidadeHook";
import { api_url } from "../../Utils/Urls";
import axios from "axios";
import { PdfView } from "../../Components/PdfView";
import { Loader } from "../../Components/Loader";

type TipoPesquisa = "cpf" | "cnpj" | "codigo";

const meses = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
];

function onlyDigits(v: string) {
    return (v || "").replace(/\D+/g, "");
}

function maskCPF(v: string) {
    const s = onlyDigits(v).slice(0, 11);
    return s
        .replace(/^(\d{3})(\d)/, "$1.$2")
        .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
}

function maskCNPJ(v: string) {
    const s = onlyDigits(v).slice(0, 14);
    return s
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
}

export async function buscaArquivo(caminho: string, pasta: string): Promise<string[] | null> {
    try {
        const { data } = await axios.get<string[]>(caminho, { timeout: 15000 });
        if (!Array.isArray(data) || data.length === 0 || typeof data[0] !== "string") {
            return null;
        }
        const arquivos = data.map((data)=> "E:\\sistemas\\unico\\" + pasta + "\\guias_agua\\" + data)
        //const arquivo = "E:\\sistemas\\unico\\" + pasta + "\\guias_agua\\" + data[0].trim();
        return arquivos;
    } catch (error) {
        console.error("Erro ao verificar arquivo:", error);
        return null; //o que reotorno aqui  já que não pode ser null?
    }
}

export const Agua: React.FC = () => {
    const { entidade } = useEntidade();
    const now = new Date();
    const anoAtual = now.getFullYear();
    const mesAtual = now.getMonth() + 1; // 1..12
    const [tipo, setTipo] = useState<TipoPesquisa>("cpf");
    const [valor, setValor] = useState("");
    // estado inicial: mês anterior; em janeiro volta para dez/ano anterior
    const [ano, setAno] = useState<number>(mesAtual === 1 ? anoAtual - 1 : anoAtual);
    const [mes, setMes] = useState<number>(mesAtual === 1 ? 12 : mesAtual - 1);
    const [erroValor, setErroValor] = useState<string | null>(null);

    const [visualizarPdfOpen, setVisualizarPdfOpen] = useState(false);
    const [nomesArquivosPdf, setNomesArquivosPdf] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);

    const anosDisponiveis = useMemo(() => {
        const base = now.getFullYear();
        return [base - 1, base, base + 1];
    }, [now]);

    const mesesOptions = useMemo(
        () => meses.map((label, idx) => ({ label, value: idx + 1 })),
        []
    );

    const mesesFiltrados = useMemo(() => {
        if (ano !== anoAtual) return mesesOptions;            // outros anos → todos
        return mesesOptions.filter((opt) => opt.value < mesAtual); // ano atual → até o mês anterior
    }, [ano, anoAtual, mesAtual, mesesOptions]);

    //garantir que mes seja válido ao trocar o ano
    useEffect(() => {
        const permitidos = new Set(mesesFiltrados.map(o => o.value));
        if (!permitidos.has(mes)) {
          const fallback = mesesFiltrados.at(-1)?.value ?? 1;
          setMes(fallback);
        }
      }, [ano, mesesFiltrados]); 

    const iconeCampo =
        tipo === "cpf" ? <BadgeIcon /> : tipo === "cnpj" ? <BusinessIcon /> : <QrCodeIcon />;

    const labelCampo =
        tipo === "cpf" ? "CPF do titular" : tipo === "cnpj" ? "CNPJ do titular" : "Código do Carnê";

    const placeholderCampo =
        tipo === "cpf" ? "000.000.000-00" : tipo === "cnpj" ? "00.000.000/0000-00" : "Ex.: 123456";

    const valorFormatado =
        tipo === "cpf" ? maskCPF(valor) : tipo === "cnpj" ? maskCNPJ(valor) : valor;

    function validar() {
        const v = onlyDigits(valor);
        if (tipo === "cpf") {
            if (v.length !== 11) return "Informe um CPF com 11 dígitos.";
        } else if (tipo === "cnpj") {
            if (v.length !== 14) return "Informe um CNPJ com 14 dígitos.";
        } else {
            if (!v || v.length < 3) return "Informe um código de carnê válido.";
        }
        return null;
    }

    function handleChangeTipo(_e: React.MouseEvent<HTMLElement>, novo: TipoPesquisa | null) {
        if (!novo) return;
        setTipo(novo);
        setValor("");
        setErroValor(null);
    }

    const handleSubmit = async () => {
        const err = validar();
        setErroValor(err);
        if (err) return;
        setLoading(true);
        try {
            const payload = {
                tipo,
                valor: onlyDigits(valor),
                mes,
                ano,
                id_entidade: entidade?.id ?? undefined,
                banco: entidade?.banco ?? undefined,
            };
            const mesFormatado = String(payload.mes).padStart(2, "0");;
            if (payload.tipo === "cpf" || payload.tipo === "cnpj") {
                const caminho = api_url + "Arquivo/pesquisar/?pasta=E:\\sistemas\\unico\\" + entidade.pasta_unico +
                    "\\guias_agua&padrao=*" + payload.ano + "_" + mesFormatado + "*_" + payload.valor + ".pdf";

                const arquivos = await buscaArquivo(caminho, entidade.pasta_unico);
                //console.log("arquivos: ", arquivos)
                if (arquivos) {
                    setNomesArquivosPdf(arquivos);
                    setVisualizarPdfOpen(true);
                } else {
                    alert("Não foi possivel encontrar o documento PDF.");
                }
            } else if (payload.tipo === "codigo") {
                const caminho = api_url + "Arquivo/pesquisar/?pasta=E:\\sistemas\\unico\\" + entidade.pasta_unico +
                    "\\guias_agua&padrao=*" + payload.ano + "_" + mesFormatado + "*_" + payload.valor + "_*";

                const arquivos = await buscaArquivo(caminho, entidade.pasta_unico);
                //console.log("arquivos: ", arquivos)
                if (Array.isArray(arquivos) && arquivos.length > 0) {
                    setNomesArquivosPdf(arquivos);
                    setVisualizarPdfOpen(true);
                } else {
                    alert("Não foi possivel encontrar o documento PDF.");
                }
            }

            console.log("Pesquisar ->", payload);
        } catch (e) {
            console.error("Erro ao buscar arquivo(s):", e);
            alert("Erro ao buscar arquivo(s).");
        }finally{
            setLoading(false);
        }
    }
    function onEnter(e: React.KeyboardEvent) {
        if (e.key === "Enter") handleSubmit();
    }

    return (
        <Box
            sx={{
                px: { xs: 2, md: 4 },
                py: { xs: 2, md: 3 },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "background.paper",
                    boxShadow: (t) => t.shadows[1],
                }}
            >
                <Avatar
                    variant="rounded"
                    src={entidade?.logo || undefined}
                    alt={entidade?.titulo || "Entidade"}
                    sx={{ width: 56, height: 56, borderRadius: 2 }}
                />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" noWrap fontWeight={700}>
                        {entidade?.titulo ?? "Entidade"}
                    </Typography>
                    {(entidade?.site || entidade?.portal || entidade?.site2) ? (
                        <Typography
                            variant="body2"
                            color="primary"
                            component="a"
                            href={entidade?.site || entidade?.portal || entidade?.site2}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                            {entidade?.site || entidade?.portal || entidade?.site2}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            Serviço de Água
                        </Typography>
                    )}

                </Box>
            </Box>

            {/* Card de busca */}
            <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                    title="Pesquisar Guias de Água"
                    subheader="Busque por CPF, CNPJ ou Código do Carnê e selecione o período"
                    sx={{
                        "& .MuiCardHeader-title": { fontWeight: 700 },
                    }}
                />
                <Divider />
                <CardContent>
                    <Stack spacing={2}>
                        {/* Botões de seleção */}
                        <ToggleButtonGroup
                            value={tipo}
                            exclusive
                            onChange={handleChangeTipo}
                            color="primary"
                            sx={{
                                alignSelf: { xs: "stretch", sm: "flex-start" },
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                            }}
                        >
                            <ToggleButton
                                value="cpf"
                                sx={{
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: 140,
                                    height: 56,
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: "none",
                                }}
                            >
                                CPF
                            </ToggleButton>
                            <ToggleButton
                                value="cnpj"
                                sx={{
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: 140,
                                    height: 56,
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: "none",
                                }}
                            >
                                CNPJ
                            </ToggleButton>
                            <ToggleButton
                                value="codigo"
                                sx={{
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: 180,
                                    height: 56,
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: "none",
                                }}
                            >
                                Código do Carnê
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Linha de filtros */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ xs: "stretch", sm: "flex-end" }}
                        >
                            <TextField
                                fullWidth
                                label={labelCampo}
                                value={valorFormatado}
                                onChange={(e) => {
                                    setErroValor(null);
                                    setValor(e.target.value);
                                }}
                                onKeyDown={onEnter}
                                placeholder={placeholderCampo}
                                error={!!erroValor}
                                helperText={erroValor || " "}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {iconeCampo}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ height: 56 }}
                            />

                            <TextField
                                select
                                label="Mês"
                                value={mes}
                                onChange={(e) => setMes(Number(e.target.value))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CalendarMonthIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    minWidth: { xs: "100%", sm: 180 },
                                    height: 56,
                                    display: "flex",
                                    "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                        height: "100%",
                                        paddingY: "14px",
                                    },
                                }}
                            >
                                {mesesFiltrados.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Ano"
                                value={ano}
                                onChange={(e) => setAno(Number(e.target.value))}
                                sx={{
                                    minWidth: { xs: "100%", sm: 140 },
                                    height: 56,
                                    display: "flex",
                                    "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                        height: "100%",
                                        paddingY: "14px",
                                    },
                                }}
                            >
                                {anosDisponiveis.map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<SearchIcon />}
                                onClick={handleSubmit}
                                sx={{
                                    minWidth: { xs: "100%", sm: 180 },
                                    height: 56,
                                    borderRadius: 2,
                                    fontWeight: 700,
                                }}
                            >
                                Pesquisar
                            </Button>
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            Dica: você pode digitar e pressionar <b>Enter</b> para pesquisar.
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            {/* Placeholder de resultados */}
            <Box mt={3}>
                <Typography variant="subtitle2" color="text.secondary">
                    Resultados aparecerão aqui após a busca.
                </Typography>
            </Box>

            <PdfView
                open={visualizarPdfOpen}
                onClose={() => setVisualizarPdfOpen(false)}
                nomeArquivo={nomesArquivosPdf}
                modoImpressao={true}
            />
                <Loader loading={loading} />
        </Box>
    );
};
